/* eslint-disable no-console */
import { datadogLogs } from "@datadog/browser-logs";
import type { PageTypes } from "@lib/enums/PageTypes";
import { hasWindow } from "./../utils/hasWindow";

export enum LogType {
    INFO = "info",
    WARN = "warn",
    ERROR = "error",
}

export enum ServiceType {
    CONTENTFUL = "Contentful",
    COMMERCE_TOOLS = "CommerceTools",
    NEXT_API = "Next.js API",
    ALGOLIA = "Algolia",
    WEB = "Web",
    ADYEN = "Adyen",
    YOTPO = "Yotpo",
    AMAZON_PAY = "AmazonPay",
    GOOGLE = "Google",
    RECAPTCHA = "Recaptcha",
    MICROSOFT = "Microsoft",
}

export enum LogTag {
    PAYMENT = "PAYMENT",
    ORDER = "ORDER",
    MICROCOPY = "MICROCOPY",
    PERFORMANCE = "PERFORMANCE",
    ERROR = "ERROR",
    SUCCESS = "SUCCESS",
    MISSING_ADDRESS_FIELDS = "MISSING_ADDRESS_FIELDS",
    MISSING_IMAGE = "MISSING_IMAGE",
    DISCOUNTS = "DISCOUNTS",
    STORES = "STORES",
    PRODUCT = "PRODUCT",
    FEED = "FEED",
    ANALYTICS = "ANALYTICS",
    ACCOUNT = "ACCOUNT",
    CHECKOUT = "CHECKOUT",
    ACCESSIBILITY = "ACCESSIBILITY",
    REVIEW = "REVIEW",
    REVALIDATION = "REVALIDATION",
    RECAPTCHA = "RECAPTCHA",
    ORDER_HISTORY = "ORDER_HISTORY",
    ADDRESS_LOOKUP = "ADDRESS_LOOKUP",
    AUTHENTICATION = "AUTHENTICATION",
    LIVE_CHAT = "LIVE_CHAT",
    CONTACT_FORM = "CONTACT_FORM",
    EXPRESS_CHECKOUT = "EXPRESS_CHECKOUT",
    SHIPPING = "SHIPPING",
    SHOPPING_BAG = "SHOPPING_BAG",
}

type LogPayload = {
    [key: string]: any;
    tag: LogTag;
    _duration?: number;
    _pageType?: PageTypes;
};

const consoleLogFunctions = {
    [LogType.ERROR]: console.error,
    [LogType.WARN]: console.warn,
    [LogType.INFO]: console.log,
};

export abstract class Logger {
    private static log(
        logType: LogType,
        serviceType: ServiceType,
        message: string,
        payload: LogPayload
    ) {
        const { tag, ...restPayload } = payload;
        const logPayload = {
            _logger: "ECCO",
            _tag: payload.tag,
            _level: logType,
            _service: serviceType,
            _message: message,
            _caller: hasWindow ? "client" : "server",
            _data: restPayload,
        };

        if (hasWindow) {
            if (process.env.NEXT_PUBLIC_LOGGING) {
                consoleLogFunctions[logType](message, logPayload);
            }

            datadogLogs.logger[logType](message, logPayload);
        } else {
            const stringifiedPayload = JSON.stringify(logPayload);

            if (process.env.NODE_ENV === "production" || process.env.NEXT_PUBLIC_LOGGING) {
                consoleLogFunctions[logType](stringifiedPayload);
            }
        }
    }

    static info(serviceType: ServiceType, message: string, payload: LogPayload) {
        this.log(LogType.INFO, serviceType, message, payload);
    }

    static warn(serviceType: ServiceType, message: string, payload: LogPayload) {
        this.log(LogType.WARN, serviceType, message, payload);
    }

    static error(serviceType: ServiceType, message: string, payload: LogPayload) {
        this.log(LogType.ERROR, serviceType, message, payload);
    }

    static errorToJson(error?: unknown) {
        if (error && (error instanceof Error || typeof error === "object")) {
            return {
                full: error instanceof Error ? error.toString() : String(error),
                name: (error as any)?.name,
                message: (error as any)?.message,
                stack: (error as any)?.stack,
                cause: (error as any)?.cause,
            };
        }
        return error;
    }
}
